import React from 'react';
import { Zap, CloudLightning, Timer, ArrowUpRight, ShieldCheck, MapPin, Plus, TrendingUp, Info, Sun, X, Check, MessageSquare, AlertTriangle, ChevronDown, Navigation } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { toast } from 'sonner';

interface PowerUpdate {
  id: string;
  status: 'grid' | 'gen' | 'solar' | 'none';
  neighborhood: string;
  state: string;
  timestamp: string;
  reporter: string;
  note?: string;
}

// Major neighborhoods across Nigeria
const NIGERIA_NEIGHBORHOODS = {
  Lagos: ['Lekki Phase 1', 'Victoria Island', 'Ikoyi', 'Ikeja', 'Surulere', 'Yaba', 'Ajah', 'Maryland', 'Festac', 'Apapa'],
  Abuja: ['Wuse II', 'Maitama', 'Asokoro', 'Garki', 'Gwarinpa', 'Kubwa', 'Lugbe', 'Jabi', 'Utako', 'Kado'],
  'Port Harcourt': ['GRA', 'Trans Amadi', 'Rumuokoro', 'Eliozu', 'Choba', 'Alakahia', 'Rumuola'],
  Ibadan: ['Bodija', 'Dugbe', 'Ring Road', 'UI Area', 'Mokola', 'Agodi'],
  Kano: ['Nasarawa', 'Sabon Gari', 'Fagge', 'Gwale', 'Municipal'],
  Enugu: ['GRA', 'Independence Layout', 'Trans-Ekulu', 'New Haven', 'Coal Camp'],
  Kaduna: ['Barnawa', 'Malali', 'Sabon Tasha', 'Narayi', 'Ungwan Rimi']
};

export function LightWatchWidget({ neighborhood }: { neighborhood: string }) {
  const [selectedState, setSelectedState] = React.useState<string>('Lagos');
  const [selectedNeighborhood, setSelectedNeighborhood] = React.useState<string>(neighborhood);
  const [showLocationPicker, setShowLocationPicker] = React.useState(false);
  const [filterByLocation, setFilterByLocation] = React.useState(false);
  
  const [reports, setReports] = React.useState<PowerUpdate[]>([
    { id: '1', status: 'grid', neighborhood: 'Lekki Phase 1', state: 'Lagos', timestamp: '2 mins ago', reporter: 'Tunde W.' },
    { id: '2', status: 'gen', neighborhood: 'Wuse II', state: 'Abuja', timestamp: '5 mins ago', reporter: 'Amina K.' },
    { id: '3', status: 'solar', neighborhood: 'Victoria Island', state: 'Lagos', timestamp: '8 mins ago', reporter: 'Chidi K.' },
    { id: '4', status: 'gen', neighborhood: 'GRA', state: 'Port Harcourt', timestamp: '12 mins ago', reporter: 'Emeka N.' },
    { id: '5', status: 'grid', neighborhood: 'Bodija', state: 'Ibadan', timestamp: '15 mins ago', reporter: 'Funmi A.' },
    { id: '6', status: 'none', neighborhood: 'Sabon Gari', state: 'Kano', timestamp: '18 mins ago', reporter: 'Usman B.' },
    { id: '7', status: 'grid', neighborhood: 'GRA', state: 'Enugu', timestamp: '22 mins ago', reporter: 'Ngozi O.' },
    { id: '8', status: 'gen', neighborhood: 'Barnawa', state: 'Kaduna', timestamp: '25 mins ago', reporter: 'Yusuf M.' }
  ]);

  const [isReporting, setIsReporting] = React.useState(false);
  const [reportNote, setReportNote] = React.useState('');
  const [selectedStatus, setSelectedStatus] = React.useState<'grid' | 'gen' | 'solar' | 'none'>('grid');
  const [targetNeighborhood, setTargetNeighborhood] = React.useState(neighborhood);

  const [lastAlert, setLastAlert] = React.useState<PowerUpdate | null>(null);
  const [showHistory, setShowHistory] = React.useState(false);

  const currentStatus = reports[0]?.status || 'none';

  const handleQuickReport = (status: 'grid' | 'gen' | 'solar' | 'none') => {
    const newReport: PowerUpdate = {
      id: Date.now().toString(),
      status,
      neighborhood: neighborhood,
      state: selectedState,
      timestamp: 'Just now',
      reporter: 'You'
    };
    setReports([newReport, ...reports]);
    setLastAlert(newReport);
    setTimeout(() => setLastAlert(null), 5000);
    toast.success('Power Status Updated', {
      description: `Reported ${status.toUpperCase()} for ${neighborhood}. Neighbors notified!`,
      icon: status === 'solar' ? <Sun className="w-4 h-4 text-amber-500" /> : <Zap className="w-4 h-4 text-amber-500" />
    });
  };

  const handleReport = (status: 'grid' | 'gen' | 'solar' | 'none') => {
    const newReport: PowerUpdate = {
      id: Date.now().toString(),
      status,
      neighborhood,
      state: selectedState,
      timestamp: 'Just now',
      reporter: 'You'
    };
    setReports([newReport, ...reports]);
    setLastAlert(newReport);
    setTimeout(() => setLastAlert(null), 5000);
    toast.success('Power Status Updated', {
      description: `Reported ${status.toUpperCase()} for ${neighborhood}. Neighbors notified!`,
      icon: status === 'solar' ? <Sun className="w-4 h-4 text-amber-500" /> : <Zap className="w-4 h-4 text-amber-500" />
    });
  };

  const handleSubmitDetailed = (e: React.FormEvent) => {
    e.preventDefault();
    const newReport: PowerUpdate = {
      id: Date.now().toString(),
      status: selectedStatus,
      neighborhood: targetNeighborhood,
      state: selectedState,
      timestamp: 'Just now',
      reporter: 'You',
      note: reportNote
    };
    setReports([newReport, ...reports]);
    setLastAlert(newReport);
    setTimeout(() => setLastAlert(null), 5000);
    setIsReporting(false);
    setReportNote('');
    toast.success('Detailed Report Sent', {
      description: `Your update about "${reportNote}" for ${targetNeighborhood} has been broadcast.`,
      icon: <Check className="w-4 h-4 text-green-500" />
    });
  };

  const handleViewAll = () => {
    // In a real app, we'd navigate here. For now, we'll just show a toast or emit an event.
    toast.info('Opening Neighborhood Power Feed', {
      description: 'Showing live light updates from surrounding areas.'
    });
    // This will work if we expose it to the parent, but for now let's just make it clear.
  };

  return (
    <div className="bg-gradient-to-br from-yellow-50 via-amber-50 to-orange-50 rounded-[32px] p-6 border-2 border-yellow-300 shadow-2xl shadow-yellow-500/30 overflow-hidden relative group">
      {/* Animated background blobs */}
      <div className="absolute top-0 right-0 w-56 h-56 bg-gradient-to-br from-yellow-400/30 to-amber-400/30 rounded-full blur-3xl animate-pulse" />
      <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-orange-400/20 to-red-400/20 rounded-full blur-3xl animate-pulse delay-500" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 bg-yellow-300/10 rounded-full blur-2xl animate-ping" />

      <AnimatePresence>
        {lastAlert && (
          <motion.div 
            initial={{ y: -50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -50, opacity: 0 }}
            className="absolute top-4 left-4 right-4 z-50 bg-green-600 text-white p-3 rounded-2xl shadow-lg flex items-center gap-3 border border-green-500/20"
          >
            <div className="bg-white/20 p-1.5 rounded-lg">
              <AlertTriangle className="w-4 h-4" />
            </div>
            <div className="flex-1">
              <p className="text-[10px] font-black uppercase tracking-tight leading-none mb-0.5">Live Community Alert</p>
              <p className="text-[8px] font-bold opacity-90 leading-none">New {lastAlert.status.toUpperCase()} report in {lastAlert.neighborhood}</p>
            </div>
            <button onClick={() => setLastAlert(null)}><X className="w-3.5 h-3.5" /></button>
          </motion.div>
        )}
      </AnimatePresence>
      <div className="flex items-center justify-between mb-6 relative z-10">
        <div>
          <h3 className="text-xs font-black bg-gradient-to-r from-yellow-600 via-amber-600 to-orange-600 bg-clip-text text-transparent uppercase tracking-widest mb-2 drop-shadow-sm flex items-center gap-2">
            ⚡ Light Watch Live • All Nigeria 🇳🇬
          </h3>
          <div className="flex items-center gap-3 p-2 bg-gradient-to-r from-yellow-500 to-amber-600 rounded-2xl shadow-lg">
            <div className={`w-3 h-3 rounded-full animate-pulse shadow-lg ${currentStatus === 'grid' ? 'bg-green-400' : currentStatus === 'solar' ? 'bg-yellow-300' : 'bg-orange-400'}`} />
            <Zap className="w-5 h-5 text-white drop-shadow-md" />
            <p className="text-base font-black uppercase text-white drop-shadow-md">{selectedNeighborhood}</p>
          </div>
          <button
            onClick={() => setShowLocationPicker(true)}
            className="mt-2 flex items-center gap-2 px-3 py-1.5 bg-purple-100 text-purple-700 rounded-xl text-xs font-black uppercase hover:bg-purple-200 transition-all active:scale-95"
          >
            <MapPin className="w-3 h-3" />
            Change Location
          </button>
        </div>
        <button 
          onClick={() => setIsReporting(true)}
          className="bg-zinc-900 text-white px-3 py-1.5 rounded-xl flex items-center gap-2 hover:bg-black transition-all active:scale-95 shadow-sm"
        >
          <MessageSquare className="w-3.5 h-3.5" />
          <span className="text-[9px] font-black uppercase tracking-widest">Report Detail</span>
        </button>
      </div>

      <AnimatePresence>
        {isReporting && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-6 overflow-hidden"
          >
            <div className="p-5 rounded-2xl bg-zinc-50 border border-zinc-100 space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-[10px] font-black text-zinc-500 uppercase tracking-widest">Submit Information</h4>
                <button onClick={() => setIsReporting(false)}><X className="w-4 h-4 text-zinc-400" /></button>
              </div>

              <div className="space-y-1.5">
                <label className="text-[9px] font-black text-zinc-400 uppercase tracking-widest ml-1">Neighborhood / Street</label>
                <div className="relative">
                  <MapPin className="w-3.5 h-3.5 text-zinc-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                  <input 
                    type="text"
                    placeholder="e.g. Lekki Phase 1, Admiralty Way..."
                    value={targetNeighborhood}
                    onChange={(e) => setTargetNeighborhood(e.target.value)}
                    className="w-full bg-white border border-zinc-200 rounded-xl pl-10 pr-4 py-2.5 text-xs font-bold text-zinc-900 placeholder:text-zinc-300 focus:outline-none focus:ring-2 focus:ring-zinc-500/10"
                  />
                </div>
              </div>

              <div className="flex gap-2">
                {(['grid', 'solar', 'gen'] as const).map((s) => (
                  <button
                    key={s}
                    onClick={() => setSelectedStatus(s)}
                    className={`flex-1 py-2 rounded-lg text-[8px] font-black uppercase tracking-widest transition-all border ${selectedStatus === s ? 'bg-zinc-900 text-white border-zinc-900' : 'bg-white text-zinc-400 border-zinc-200'}`}
                  >
                    {s === 'grid' ? 'NEPA' : s}
                  </button>
                ))}
              </div>

              <div className="space-y-1.5">
                <label className="text-[9px] font-black text-zinc-400 uppercase tracking-widest ml-1">What's happening?</label>
                <textarea 
                  placeholder="e.g. Transformer spark, Low voltage, or Maintenance alert..."
                  value={reportNote}
                  onChange={(e) => setReportNote(e.target.value)}
                  className="w-full bg-white border border-zinc-200 rounded-xl px-4 py-3 text-xs font-bold text-zinc-900 placeholder:text-zinc-300 focus:outline-none focus:ring-2 focus:ring-zinc-500/10 min-h-[80px] resize-none"
                />
              </div>

              <button 
                onClick={handleSubmitDetailed}
                disabled={!reportNote || !targetNeighborhood}
                className="w-full bg-green-600 text-white py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-green-700 transition-all active:scale-[0.98] disabled:opacity-50"
              >
                Send Community Alert
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid grid-cols-3 gap-3 mb-6 relative z-10">
        <div className={`p-4 rounded-2xl border-2 transition-all shadow-lg ${currentStatus === 'grid' ? 'bg-gradient-to-br from-green-400 to-emerald-600 border-green-300 shadow-green-500/50 scale-105' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center gap-2 mb-2">
            <Zap className={`w-4 h-4 ${currentStatus === 'grid' ? 'text-white drop-shadow-md animate-pulse' : 'text-gray-400'}`} />
            <span className={`text-xs font-black uppercase ${currentStatus === 'grid' ? 'text-white drop-shadow-md' : 'text-gray-500'}`}>NEPA</span>
          </div>
          <p className={`text-base font-black italic uppercase ${currentStatus === 'grid' ? 'text-white drop-shadow-md' : 'text-gray-400'}`}>
            {currentStatus === 'grid' ? '⚡ Up' : 'Down'}
          </p>
        </div>
        <div className={`p-4 rounded-2xl border-2 transition-all shadow-lg ${currentStatus === 'solar' ? 'bg-gradient-to-br from-yellow-400 to-orange-500 border-yellow-300 shadow-yellow-500/50 scale-105' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center gap-2 mb-2">
            <Sun className={`w-4 h-4 ${currentStatus === 'solar' ? 'text-white drop-shadow-md animate-pulse' : 'text-gray-400'}`} />
            <span className={`text-xs font-black uppercase ${currentStatus === 'solar' ? 'text-white drop-shadow-md' : 'text-gray-500'}`}>Solar</span>
          </div>
          <p className={`text-base font-black italic uppercase ${currentStatus === 'solar' ? 'text-white drop-shadow-md' : 'text-gray-400'}`}>
            {currentStatus === 'solar' ? '☀️ Full' : 'Idle'}
          </p>
        </div>
        <div className={`p-4 rounded-2xl border-2 transition-all shadow-lg ${currentStatus === 'gen' ? 'bg-gradient-to-br from-gray-700 to-gray-900 border-gray-600 shadow-gray-800/50 scale-105' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center gap-2 mb-2">
            <CloudLightning className={`w-4 h-4 ${currentStatus === 'gen' ? 'text-white drop-shadow-md animate-pulse' : 'text-gray-400'}`} />
            <span className={`text-xs font-black uppercase ${currentStatus === 'gen' ? 'text-white drop-shadow-md' : 'text-gray-500'}`}>Gen</span>
          </div>
          <p className={`text-base font-black italic uppercase ${currentStatus === 'gen' ? 'text-white drop-shadow-md' : 'text-gray-400'}`}>
            {currentStatus === 'gen' ? '🔥 On' : 'Off'}
          </p>
        </div>
      </div>

      <div className="space-y-3 relative z-10">
        <p className="text-xs font-black bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent uppercase tracking-widest">✨ Update Your Street</p>
        <div className="flex gap-3">
          <button 
            onClick={() => handleReport('grid')}
            className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 text-white py-3 rounded-2xl text-xs font-black uppercase tracking-widest active:scale-95 transition-all shadow-lg shadow-green-500/30 hover:shadow-green-500/50"
          >
            ⚡ I have NEPA
          </button>
          <button 
            onClick={() => handleReport('solar')}
            className="flex-1 bg-gradient-to-r from-yellow-400 to-orange-500 text-white py-3 rounded-2xl text-xs font-black uppercase tracking-widest active:scale-95 transition-all shadow-lg shadow-yellow-500/30 hover:shadow-yellow-500/50"
          >
            ☀️ On Solar
          </button>
          <button 
            onClick={() => handleReport('gen')}
            className="flex-1 bg-gradient-to-r from-gray-700 to-gray-900 text-white py-3 rounded-2xl text-xs font-black uppercase tracking-widest active:scale-95 transition-all shadow-lg shadow-gray-700/30 hover:shadow-gray-700/50"
          >
            🔥 Using Gen
          </button>
        </div>
      </div>

      <div className="mt-6 pt-6 border-t border-zinc-100">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-3.5 h-3.5 text-blue-500" />
            <p className="text-[9px] font-bold text-zinc-500 uppercase tracking-tighter">Community Feed</p>
          </div>
          <button 
            onClick={() => setShowHistory(true)}
            className="text-[9px] font-black text-blue-600 uppercase tracking-widest hover:text-blue-700"
          >
            View All
          </button>
        </div>

        <AnimatePresence>
          {showHistory && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
              onClick={() => setShowHistory(false)}
            >
              <motion.div 
                initial={{ y: 100 }}
                animate={{ y: 0 }}
                exit={{ y: 100 }}
                className="bg-white w-full max-w-lg rounded-[32px] overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6 border-b border-zinc-100 flex items-center justify-between bg-zinc-50/50">
                  <div>
                    <h3 className="text-xs font-black text-zinc-900 uppercase tracking-widest">Neighborhood Light History</h3>
                    <p className="text-[10px] text-zinc-500 font-bold uppercase">{neighborhood} & Surrounding Areas</p>
                  </div>
                  <button 
                    onClick={() => setShowHistory(false)}
                    className="w-8 h-8 rounded-full bg-white border border-zinc-200 flex items-center justify-center text-zinc-400 hover:text-zinc-900 transition-colors shadow-sm"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                
                <div className="p-6 max-h-[60vh] overflow-y-auto space-y-4">
                  {reports.map((report) => (
                    <div
                      key={report.id}
                      className="bg-zinc-50 rounded-2xl p-4 border border-zinc-100"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center shadow-sm ${
                            report.status === 'grid' ? 'bg-green-100 text-green-600' : 
                            report.status === 'solar' ? 'bg-amber-100 text-amber-600' : 
                            report.status === 'gen' ? 'bg-zinc-900 text-white' : 'bg-zinc-100 text-zinc-400'
                          }`}>
                            {report.status === 'grid' ? <Zap className="w-5 h-5" /> : 
                             report.status === 'solar' ? <Sun className="w-5 h-5" /> : 
                             <CloudLightning className="w-5 h-5" />}
                          </div>
                          <div>
                            <p className="text-xs font-black text-zinc-900 uppercase italic">
                              {report.reporter} <span className="text-zinc-400 font-medium not-italic ml-1">reported {report.status === 'grid' ? 'NEPA' : report.status}</span>
                            </p>
                            <p className="text-[9px] font-bold text-zinc-500 uppercase tracking-widest flex items-center gap-1.5 mt-0.5">
                              <MapPin className="w-2.5 h-2.5" /> {report.neighborhood} • {report.timestamp}
                            </p>
                          </div>
                        </div>
                      </div>
                      {report.note && (
                        <div className="mt-3 bg-white/50 p-3 rounded-xl border border-zinc-100/50">
                          <p className="text-[11px] font-medium text-zinc-600 leading-relaxed italic">
                            "{report.note}"
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                
                <div className="p-6 bg-zinc-50 border-t border-zinc-100">
                  <button 
                    onClick={() => setShowHistory(false)}
                    className="w-full bg-zinc-900 text-white py-4 rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] shadow-lg active:scale-[0.98] transition-all"
                  >
                    Close History
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
        
        <div className="space-y-3 max-h-[200px] overflow-y-auto pr-2 scrollbar-hide">
          <AnimatePresence initial={false}>
            {reports.map((report) => (
              <motion.div
                key={report.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-zinc-50 rounded-2xl p-3 border border-zinc-100"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      report.status === 'grid' ? 'bg-green-100 text-green-600' : 
                      report.status === 'solar' ? 'bg-amber-100 text-amber-600' : 
                      report.status === 'gen' ? 'bg-zinc-800 text-white' : 'bg-zinc-100 text-zinc-400'
                    }`}>
                      {report.status === 'grid' ? <Zap className="w-4 h-4" /> : 
                       report.status === 'solar' ? <Sun className="w-4 h-4" /> : 
                       <CloudLightning className="w-4 h-4" />}
                    </div>
                    <div>
                      <p className="text-[10px] font-black text-zinc-900 uppercase italic">
                        {report.reporter} <span className="text-zinc-400 font-medium not-italic ml-1">reported {report.status === 'grid' ? 'NEPA' : report.status}</span>
                      </p>
                      <p className="text-[8px] font-bold text-zinc-400 uppercase tracking-widest flex items-center gap-1">
                        <MapPin className="w-2 h-2" /> {report.neighborhood} • {report.timestamp}
                      </p>
                    </div>
                  </div>
                  {report.note && (
                    <div className="bg-white px-2 py-1 rounded-lg border border-zinc-100">
                      <MessageSquare className="w-3 h-3 text-zinc-400" />
                    </div>
                  )}
                </div>
                {report.note && (
                  <p className="mt-2 text-[10px] font-medium text-zinc-600 leading-relaxed border-l-2 border-zinc-200 pl-2">
                    "{report.note}"
                  </p>
                )}
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}